export const hasPermissions = (
  requiredPermissions = [],
  availablePermissions = []
) => {
  return requiredPermissions.some(permission =>
    availablePermissions.includes(permission)
  );
};

export const getCurrentAccount = ({ accounts } = {}, accountId = null) => {
  return accounts.find(account => Number(account.id) === Number(accountId));
};

export const getUserPermissions = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  return currentAccount.permissions || [];
};

export const getUserRole = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  if (currentAccount.custom_role_id) {
    return 'custom_role';
  }

  return currentAccount.role || 'agent';
};

/**
 * Returns true when the current user can access the given conversation ONLY because
 * of the `conversation_resolved_view` permission (i.e. they have no manage permission
 * that would grant them write access to this specific conversation).
 *
 * Mirrors the backend logic in Enterprise::ConversationPolicy#update?.
 *
 * @param {Object} currentUser - User object from the Vuex store (getCurrentUser).
 * @param {number|string} accountId - Current account ID.
 * @param {Object} conversation - The conversation object (currentChat).
 * @returns {boolean}
 */
export const isConversationResolvedViewOnly = (
  currentUser,
  accountId,
  conversation
) => {
  if (conversation?.status !== 'resolved') return false;

  const userPermissions = getUserPermissions(currentUser, accountId);

  if (!userPermissions.includes('conversation_resolved_view')) return false;

  // Full manage covers all conversations — not read-only
  if (userPermissions.includes('conversation_manage')) return false;

  const assigneeId = conversation.meta?.assignee?.id;
  const isAssignedToMe = assigneeId === currentUser?.id;
  const isUnassigned = !assigneeId;

  // unassigned_manage grants write access when conversation is unassigned or self-assigned
  if (userPermissions.includes('conversation_unassigned_manage')) {
    if (isUnassigned || isAssignedToMe) return false;
  }

  // participating_manage grants write access when the user is the assignee
  // (participant check requires extra API data — assignee check is sufficient for UX)
  if (userPermissions.includes('conversation_participating_manage')) {
    if (isAssignedToMe) return false;
  }

  // team_manage grants write access when the conversation belongs to a team
  // (team membership is validated by the backend; assume write if team_id is present)
  if (userPermissions.includes('conversation_team_manage')) {
    if (conversation?.meta?.team?.id) return false;
  }

  return true;
};

/**
 * Returns true when the current user can access the given conversation ONLY because
 * of the `conversation_all_view` permission (i.e. they have no manage permission
 * that would grant them write access to this specific conversation).
 *
 * @param {Object} currentUser - User object from the Vuex store (getCurrentUser).
 * @param {number|string} accountId - Current account ID.
 * @param {Object} conversation - The conversation object (currentChat).
 * @returns {boolean}
 */
export const isConversationAllViewOnly = (currentUser, accountId, conversation) => {
  const userPermissions = getUserPermissions(currentUser, accountId);

  if (!userPermissions.includes('conversation_all_view')) return false;

  if (userPermissions.includes('conversation_manage')) return false;

  const assigneeId = conversation?.meta?.assignee?.id;
  const isAssignedToMe = assigneeId === currentUser?.id;
  const isUnassigned = !assigneeId;

  if (userPermissions.includes('conversation_unassigned_manage')) {
    if (isUnassigned || isAssignedToMe) return false;
  }

  if (userPermissions.includes('conversation_participating_manage')) {
    if (isAssignedToMe) return false;
  }

  if (userPermissions.includes('conversation_team_manage')) {
    if (conversation?.meta?.team?.id) return false;
  }

  return true;
};

/**
 * Filters and transforms items based on user permissions.
 *
 * @param {Object} items - An object containing items to be filtered.
 * @param {Array} userPermissions - Array of permissions the user has.
 * @param {Function} getPermissions - Function to extract required permissions from an item.
 * @param {Function} [transformItem] - Optional function to transform each item after filtering.
 * @returns {Array} Filtered and transformed items.
 */
export const filterItemsByPermission = (
  items,
  userPermissions,
  getPermissions,
  transformItem = (key, item) => ({ key, ...item })
) => {
  // Helper function to check if an item has the required permissions
  const hasRequiredPermissions = item => {
    const requiredPermissions = getPermissions(item);
    return (
      requiredPermissions.length === 0 ||
      hasPermissions(requiredPermissions, userPermissions)
    );
  };

  return Object.entries(items)
    .filter(([, item]) => hasRequiredPermissions(item)) // Keep only items with required permissions
    .map(([key, item]) => transformItem(key, item)); // Transform each remaining item
};
